/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

const url = new URL(window.location.href)
;(async () => {
  const isContactFormPage = url.pathname.startsWith('/pages') && document.querySelector('.cc-contact-form')
  if (!isContactFormPage) return

  const productHandle = url.searchParams.get('product')
  const form = document.querySelector('form[action*="/contact"]')
  if (!productHandle || !form) return
  const product = (await fetch(`/products/${productHandle}.json`).then(r => r.json()))?.product
  if (!product || !product.id) return

  const adminUrl = `https://admin.shopify.com/store/${window.Shopify.shop.replace('.myshopify.com', '')}/products/${product.id}`
  const html = `
    <input type="hidden" name="product_admin_url" value="${adminUrl}">
    <input type="hidden" name="product_url" value="https://${window.Shopify.shop}/products/${product.handle}">
  `

  const container = document.createElement('div')
  container.innerHTML = html

  form.insertAdjacentElement('afterbegin', container)
})();
if (url.searchParams.has('contact_posted')) {
  // replaceState and remove this parameter
  url.searchParams.delete('contact_posted')
  history.replaceState(null, '', url.href)
}

function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class ProductInfoCE extends XThemeBaseElement {
  mount() {
    const skuEl = document.querySelectorAll('[data-product-sku]')
    this.addEventListener('on:variant:change', e => {
      const variant = e.detail.variant
      const variantAvailable = variant && variant.available;
      this.setAttribute('data-variant-available', variantAvailable.toString());
      const sku = variant?.sku || ''

      for (let i = 0, l = skuEl.length; i < l; i += 1) {
        skuEl[i].textContent = sku || ''
      }

      const productContactModal = document.getElementById(`modal-product-${e.detail.product.id}-contact`)
      const productContactModalSku = productContactModal?.querySelector('input[name][data-sku]')
      if (productContactModalSku) productContactModalSku.value = sku
    }, { signal: this._ac.signal });
  }
}
if (!window.customElements.get('product-info')) {
  window.customElements.define('product-info', ProductInfoCE);
}

const ARROW_STATIC_SIDE = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
}

window.__is_touch_device = window.matchMedia('(pointer: coarse)').matches
window.addEventListener('resize', () => {
  window.__is_touch_device = window.matchMedia('(pointer: coarse)').matches
})

class XTooltipTrigger extends XThemeBaseElement {
  static _tooltips_container = null
  static getTooltipsContainer() {
    let el = this._tooltips_container

    if (!el) {
      el = document.createElement('div')
      document.body.appendChild(el)
      el.setAttribute('data-popper-tooltips-container', '')
      this._tooltips_container = el
    }

    return el
  }

  static _module = null
  static async getFloatingUiScript() {
    if (XTooltipTrigger._module) return XTooltipTrigger._module
    const url = window.theme?.scripts?.floatingUi
    if (!url) return null
    const module = await import(url)
    XTooltipTrigger._module = module
    return module
  }

  constructor() {
    super()
    this.showTooltipFloating = this.showTooltipFloating.bind(this)
    this.hideTooltipFloating = this.hideTooltipFloating.bind(this)
    this.updateFloatingUi = this.updateFloatingUi.bind(this)
    this.handleTooltipFocusOut = this.handleTooltipFocusOut.bind(this)
    this.handleTooltipMouseLeave = this.handleTooltipMouseLeave.bind(this)
  }

  async mount() {
    if (this._init) return
    await XTooltipTrigger.getFloatingUiScript()

    this._init = true

    let trigger = this instanceof HTMLButtonElement ? this : this.querySelector('button')

    let tooltipId = this.getAttribute('data-popper-trigger')
    let tooltip = document.getElementById(tooltipId)
    if (!tooltip || !trigger) return

    const tooltipsContainer = XTooltipTrigger.getTooltipsContainer()
    if (tooltip.parentElement !== tooltipsContainer) {
      tooltipsContainer.appendChild(tooltip)
    }

    let config = {}
    try {
      config = JSON.parse(tooltip.dataset.popperConfig)
    } catch (e) {}

    const arrowElement = tooltip.querySelector('[data-popper-arrow]') || null

    this.config = Object.assign(config, {
      placement: config.placement || 'right',
      middleware: [
        FloatingUIDOM.offset(6),
        FloatingUIDOM.autoPlacement({ crossAxis: true }),
        FloatingUIDOM.shift({ padding: 5 }),
        arrowElement ? FloatingUIDOM.arrow({ element: arrowElement }) : null,
      ].filter(Boolean),
    })

    this.els = { trigger, tooltip, arrowElement }
    ;[
      ['mouseenter', this.showTooltipFloating],
      ['mouseleave', this.hideTooltipFloating],
      ['focus', this.showTooltipFloating],
      ['blur', this.hideTooltipFloating],
    ].forEach(([event, listener]) => {
      trigger.addEventListener(event, listener, { signal: this._ac.signal })
    })

    tooltip.addEventListener('mouseleave', this.handleTooltipMouseLeave, {
      signal: this._ac.signal,
    })
    tooltip.addEventListener('focusout', this.handleTooltipFocusOut, { signal: this._ac.signal })

    // trigger.addEventListener('click', e => {
    //   this.showTooltipFloating()
    // }, { signal: this._ac.signal })
  }

  updateFloatingUi() {
    const config = this.config
    const { trigger, tooltip, arrowElement } = this.els

    FloatingUIDOM.computePosition(trigger, tooltip, config).then(
      ({ x, y, placement, middlewareData }) => {
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
        })

        // arrow styles
        if (arrowElement) {
          const { x: arrowX, y: arrowY } = middlewareData.arrow
          const staticSide = ARROW_STATIC_SIDE[placement.split('-')[0]]

          Object.assign(arrowElement.style, {
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            right: '',
            bottom: '',
            [staticSide]: '-4px',
          })
        }
      }
    )
  }

  showTooltipFloating() {
    const { tooltip, trigger } = this.els
    tooltip.setAttribute('data-show', '')
    this.updateFloatingUi()

    this._cleanupAutoUpdate = FloatingUIDOM.autoUpdate(trigger, tooltip, this.updateFloatingUi)
  }

  hideTooltipFloating(e) {
    const eventType = e && e.type
    const { trigger, tooltip } = this.els;

    if (window.__is_touch_device && eventType === 'mouseleave') {
      return
    }

    // For mouseleave events, check if the mouse is entering the tooltip
    if (eventType === 'mouseleave' && e.relatedTarget && tooltip.contains(e.relatedTarget)) {
      return
    }

    // For blur events, check if the focus is moving into the tooltip
    if (
      eventType === 'blur' &&
      e.relatedTarget &&
      (tooltip.contains(e.relatedTarget) || trigger.contains(e.relatedTarget))
    ) {
      return
    }

    tooltip.removeAttribute('data-show')

    if (this._cleanupAutoUpdate) {
      this._cleanupAutoUpdate()
      this._cleanupAutoUpdate = null
    }
  }

  handleTooltipMouseLeave(e) {
    const { trigger, tooltip } = this.els
    // If the mouse leaves the tooltip and isn't going back to the trigger, hide it
    if (e.relatedTarget && !trigger.contains(e.relatedTarget)) {
      this.hideTooltipFloating(e)
    }
  }

  handleTooltipFocusOut(e) {
    const { trigger, tooltip } = this.els
    // If focus leaves the tooltip and isn't going back to the trigger, hide it
    if (e.relatedTarget && !trigger.contains(e.relatedTarget) && !tooltip.contains(e.relatedTarget)
    ) {
      this.hideTooltipFloating(e)
    } else if (!e.relatedTarget) {
      // If focus moves to nowhere (e.g., tabbing out of the window)
      this.hideTooltipFloating(e)
    }
  }

  unmount() {
    try {
      this.popperInstance?.destroy?.()
      this.popperInstance = null
    } catch (e) {}

    if (this._cleanupAutoUpdate) {
      this._cleanupAutoUpdate()
      this._cleanupAutoUpdate = null
    }

    console.log('UNMOUNTING', this)
    this.els = null
    this.config = null
    this._init = false
  }
}
if (!window.customElements.get('x-tooltip-trigger')) {
  window.customElements.define('x-tooltip-trigger', XTooltipTrigger);
}

// Affirm Custom Element
;(function() {
  let _affirm_api_key_custom = '7D4QAYOFFVICQXJU'

  function updateAffirmPrice(price) {
    let affirmApiKey = (window._affirm_config && window._affirm_config.public_api_key) || _affirm_api_key_custom
    if (!affirmApiKey) return Promise.reject('No Affirm API Key')
  
    const url = `https://www.affirm.com/api/promos/v2/${affirmApiKey}?amount=${price}&field=ala&locale=en_US&logo_color=blue&logo_type=logo&page_type=product&show_cta=true&`
  
    return fetch(url).then(r => r.json())
    .then(data => {
      let installmentPrice = null
      if (data.promo) {
        installmentPrice = ((data.promo.ala || '').match(/\$\d+(\.\d+)?/) || [])[0] || null
      }
      return installmentPrice
    })
    .catch(e => {
      console.log('Affirm fetch error...')
      console.error(e)
      return null
    })
  }
  
  class AffirmPriceCustom extends HTMLElement {
    constructor() {
      super();
  
      // Element functionality written in here
      this._installmentPriceEl = Array.from(this.querySelectorAll('[data-installment]'))
    }

    _updateElementPrice(p) {
       return updateAffirmPrice(p)
        .then(installmentPrice => {
          if (!installmentPrice) {
            this.style.visibility = 'hidden'
            return
          }
  
          for (let i = 0, l = this._installmentPriceEl.length; i < l; i += 1) {
            const el = this._installmentPriceEl[i]
            el.textContent = installmentPrice
          }

          this.style.removeProperty('visibility')
        })
    }
  
    connectedCallback() {
      // console.log('Custom square element added to page.');
      const currentPrice = this.getAttribute('price')
      if (currentPrice) {
       // this._updateElementPrice(currentPrice)
      }

      const iconSpans = this.querySelectorAll('[data-affirm-icon]')
      for (let i = 0, l = iconSpans.length; i < l; i += 1) {
        const el = iconSpans[i]
        el.outerHTML = `<svg width="429" height="171" viewBox="0 0 429 171" fill="none" xmlns="http://www.w3.org/2000/svg">
<g>
<path fill-rule="evenodd" clip-rule="evenodd" d="M28.1 156.27C22.8 156.27 20.1 153.66 20.1 149.37C20.1 141.37 29.02 138.68 45.29 136.95C45.29 147.61 38.08 156.27 28.05 156.27H28.1ZM35.1 96.3198C23.47 96.3198 10.1 101.79 2.83998 107.58L9.46998 121.58C15.29 116.25 24.7 111.69 33.19 111.69C41.26 111.69 45.72 114.39 45.72 119.83C45.72 123.48 42.77 125.33 37.19 126.06C16.33 128.76 -0.0200195 134.52 -0.0200195 150.59C-0.0200195 163.33 9.04998 171.04 23.22 171.04C33.34 171.04 42.34 165.42 46.62 158.04V169.04H65.48V122.95C65.48 103.95 52.29 96.2798 35.11 96.2798L35.1 96.3198Z" fill="black"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M224.39 98.3902V168.95H244.57V134.95C244.57 118.8 254.35 114.05 261.16 114.05C264.226 114.026 267.229 114.914 269.79 116.6L273.48 97.9502C270.62 96.8056 267.559 96.2511 264.48 96.3202C254.11 96.3202 247.59 100.91 243.29 110.25V98.3902H224.39Z" fill="black"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M367 96.3198C356.33 96.3198 348.35 102.62 344.2 108.7C340.35 100.85 332.2 96.3198 322.4 96.3198C311.74 96.3198 304.35 102.24 300.94 109.06V98.3898H281.48V168.95H301.68V132.62C301.68 119.62 308.51 113.33 314.88 113.33C320.65 113.33 325.95 117.06 325.95 126.69V168.95H346.11V132.62C346.11 119.43 352.77 113.33 359.44 113.33C364.78 113.33 370.44 117.21 370.44 126.55V168.95H390.6V120.17C390.6 104.32 379.93 96.3198 367.04 96.3198" fill="black"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M175.28 98.3901H157V91.2201C157 81.8801 162.33 79.2201 166.92 79.2201C170.054 79.261 173.135 80.0313 175.92 81.4701L182.14 67.2401C182.14 67.2401 175.83 63.1201 164.36 63.1201C151.47 63.1201 136.8 70.3901 136.8 93.2001V98.3901H106.25V91.2201C106.25 81.8801 111.57 79.2201 116.17 79.2201C119.309 79.2199 122.4 79.9926 125.17 81.4701L131.39 67.2401C127.68 65.0701 121.71 63.1201 113.62 63.1201C100.73 63.1201 86.0599 70.3901 86.0599 93.2001V98.3901H74.3799V113.95H86.0899V168.95H106.25V113.95H136.84V168.95H157V113.95H175.28V98.3901Z" fill="black"/>
<path d="M207.46 98.3901H187.32V168.92H207.46V98.3901Z" fill="black"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M188.06 86.4H207.79C219.3 50.21 258.35 18.4 304.79 18.4C361.27 18.4 410.08 61.4 410.08 128.34C410.252 142.08 408.364 155.769 404.48 168.95H423.63L423.82 168.29C427.047 155.241 428.639 141.842 428.56 128.4C428.56 53.75 374.16 0.0200195 304.83 0.0200195C250.37 0.0200195 201.83 37.82 188.07 86.42L188.06 86.4Z" fill="#4A4AF4"/>
</g>
<defs>
<clipPath id="clip0_2525_303">
<rect width="428.55" height="171" fill="white"/>
</clipPath>
</defs>
</svg>`
      }
    }
  
    attributeChangedCallback(name, oldValue, newValue) {
      // console.log('Custom square element attributes changed.');
      if (name === 'amount') {
        this._updateElementPrice(newValue)
      }
    }
  
    static get observedAttributes() { return ['amount'] }
  }
  customElements.define('x-affirm-price-custom', AffirmPriceCustom);
})();
// Fix: "Choose Options" on configurator products → navigate to product page
// Works for all brands (HRE, Vossen, and any future brand using custom templates)
document.addEventListener('click', function(e) {
  var btn = e.target.closest('.js-quick-add[data-product-default-variant="false"]');
  if (!btn) return;
  var url = btn.dataset.productUrl || '';
  if (!url) return;
  e.stopImmediatePropagation();
  e.preventDefault();
  window.location.href = url;
}, true);